/**
 * useUnlockedVideos Hook
 *
 * Feature-specific hook for the Unlocked Videos page.
 *
 * Architecture: Page → Hook → UseCase → Service
 *
 * Mirrors Flutter's UnlockedContentScreenState
 * (lib/presentation/screens/content/unlocked_content_screen.dart).
 *
 * Responsibilities:
 * - Snapshot the locally-stored unlocked-content paths once on init via
 *   `unlockedVideosService.listLocalUnlockedContent()` and paginate against
 *   that snapshot, matching `_getData` (Flutter lines 109-155).
 * - Listen reactively to secure-storage writes under the
 *   `UNLOCKED_CONTENT` prefix; when a brand-new path appears, resolve the
 *   matching media and prepend it. Deletions are ignored (Flutter
 *   `_onBoxDataEvent` returns early on `event.deleted`, line 87).
 * - Surface loading / loading-more / hasMore / error to the UI.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useListenToSecureStorageUpdatesByPrefix } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { UnlockedContentStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { RootState } from '@/presentation/store';
import { unlockedVideosService } from '@/domain/services/unlockedContent/unlockedVideosService';
import { fetchUnlockedVideosPageUseCase } from '@/domain/usecases/unlockedContent/fetchUnlockedVideosPageUseCase';
import { getUserMediaByPathUseCase } from '@/domain/usecases/userMedia/getUserMediaByPathUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useLocale } from '@/presentation/contexts/LocaleContext';

export interface UseUnlockedVideosResult {
  medias: UserMediaEntity[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  init: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export const useUnlockedVideos = (): UseUnlockedVideosResult => {
  const { l10n } = useLocale();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Mirror `currentUser` into a ref so the live-update effect doesn't re-run
  // every time the auth listener mirrors SecureStorage into Redux (which
  // produces a fresh `UserEntity` reference even when nothing relevant
  // changed — see .claude/context/state-slices.md). The use cases inside the
  // live-update IIFE need the full object, so ref-mirroring is cleaner than
  // pulling out scalars.
  const currentUserRef = useRef(currentUser);
  useEffect(() => {
    currentUserRef.current = currentUser;
  });

  // Pure live-update channel — `loadInitialItems: false` because `init()`
  // below loads the existing items explicitly through the service. The
  // hook's `items` starts empty and only grows when a new unlock is
  // written while the page is open.
  const { items: liveLocalItems } =
    useListenToSecureStorageUpdatesByPrefix<UnlockedContentStorage>(
      SECURE_STORAGE_KEYS.UNLOCKED_CONTENT,
      (item) => item.path,
      false,
    );

  const [medias, setMedias] = useState<UserMediaEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The snapshot is taken once on init and is the basis for every page
  // fetch — matches Flutter's `_unlockedContentItems`, which is read once
  // (synchronously, in `initState`) and re-sliced by `_getData`.
  const localItemsSnapshotRef = useRef<UnlockedContentStorage[]>([]);
  const pageIndexRef = useRef(0);
  // Every path already covered by the pagination snapshot. With the live
  // hook in `loadInitialItems: false` mode, `liveLocalItems` only contains
  // post-mount writes — so this set is mainly defensive: it prevents the
  // live-update effect from prepending a duplicate if a write happens to
  // arrive for a path that was also in the snapshot (e.g. a race between
  // `listLocalUnlockedContent` and `setEncrypted`).
  const seenPathsRef = useRef<Set<string>>(new Set());
  // Guard so the live-update effect doesn't run before init has resolved
  // (otherwise initial paths would be treated as "new" and double-fetched).
  const hasInitializedRef = useRef(false);
  const isLoadingMoreRef = useRef(false);

  const init = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const snapshot = await unlockedVideosService.listLocalUnlockedContent();
      localItemsSnapshotRef.current = snapshot;
      pageIndexRef.current = 0;

      const result = await fetchUnlockedVideosPageUseCase.execute({
        localItems: snapshot,
        pageIndex: 0,
        currentUser,
      });

      // Seed with EVERY path in the snapshot — see the ref's declaration
      // for the rationale.
      const initialSeen = new Set<string>();
      for (const item of snapshot) {
        initialSeen.add(item.path);
      }
      seenPathsRef.current = initialSeen;

      setMedias(result.medias);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error fetching unlocked videos', 'useUnlockedVideos.init', { error: err as Error });
      setError(l10n.unlockedVideos.errorLoading);
      setMedias([]);
      setHasMore(false);
    } finally {
      // Set the ref before flipping isLoading: the live-update effect
      // gates on !isLoading and reads this ref, so the ref must be true
      // by the time the re-render from setIsLoading(false) fires it.
      hasInitializedRef.current = true;
      setIsLoading(false);
    }
  }, [currentUser, l10n]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (isLoadingMoreRef.current || !hasMore) {
      return;
    }

    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    setError(null);

    try {
      const nextIndex = pageIndexRef.current + 1;
      const result = await fetchUnlockedVideosPageUseCase.execute({
        localItems: localItemsSnapshotRef.current,
        pageIndex: nextIndex,
        currentUser,
      });

      pageIndexRef.current = nextIndex;
      setMedias((prev) => [...prev, ...result.medias]);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error loading more unlocked videos', 'useUnlockedVideos.loadMore', { error: err as Error });
      setError(l10n.unlockedVideos.loadMoreFailed);
    } finally {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [hasMore, currentUser, l10n]);

  // Reactive prepend for newly-unlocked content — mirrors Flutter
  // `_onBoxDataEvent` (lines 80-107). Deletions are intentionally ignored.
  // `isLoading` is a dep (not just the ref) so the effect re-runs after
  // init finishes — covers the race where a live write lands between
  // `listLocalUnlockedContent` resolving and `setIsLoading(false)`.
  useEffect(() => {
    if (!hasInitializedRef.current || isLoading) {
      return;
    }
    if (liveLocalItems.length === 0) {
      return;
    }

    const newPaths = liveLocalItems
      .map((item) => item.path)
      .filter((path) => !seenPathsRef.current.has(path));

    if (newPaths.length === 0) {
      return;
    }

    let cancelled = false;

    (async () => {
      for (const path of newPaths) {
        try {
          const media = await getUserMediaByPathUseCase.execute(path, currentUserRef.current);
          if (cancelled) {
            return;
          }
          // Mark as seen even if filtered out, to avoid re-fetching it on
          // every subsequent storage event.
          seenPathsRef.current.add(path);
          if (media) {
            setMedias((prev) => [media, ...prev]);
          }
        } catch (err) {
          appLogger.error(
            'Error resolving newly-unlocked media',
            'useUnlockedVideos.onLiveAdd',
            { error: err as Error, context: { path } },
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [liveLocalItems, isLoading]);

  return {
    medias,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    init,
    loadMore,
  };
};
